import { useTranslation } from 'react-i18next'
import { Badge, CardContent, CardDashboard, FlagAvatar, Flex } from '@percent/lemonade'
import { useCountries, useLocale } from '@percent/cause-dashboard/common/hooks'
import { OrganisationDetailsProps } from './OrganisationDetails.types'

export function OrganisationDetails({ organisation }: Readonly<OrganisationDetailsProps>) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const countries = useCountries()

  const countryName = countries.find(({ code }) => code === organisation.countryCode)?.name || organisation.countryCode

  const activityTags = organisation?.eligibilityResult?.confirmedActivitySubTags ?? []

  // Activity tag names are only available in English from the API.
  const showActivityTags = locale.startsWith('en')

  const valueOrNotAvailableString = (value?: string | null) => value || t('typography.notAvailable')

  return (
    <Flex direction="column" data-testid="organization-details-container">
      <CardDashboard title={t('grants.grantDetails.organizationDetails.title')} data-testid="organization-details-card">
        <CardContent title={t('grants.grantDetails.organizationDetails.name')} testId="organization-name">
          {organisation.name}
        </CardContent>
        {showActivityTags && (
          <CardContent
            title={t('grants.grantDetails.organizationDetails.activity')}
            testId="organization-activity-tags"
          >
            {activityTags.length > 0 ? (
              <Flex direction="row" gap={8} wrap="wrap" data-testid="activity-tags">
                {activityTags.map(activityTag => (
                  <Badge key={activityTag.id} variant="default" icon="categories">
                    {activityTag.name}
                  </Badge>
                ))}
              </Flex>
            ) : (
              t('typography.notAvailable')
            )}
          </CardContent>
        )}
        <CardContent title={t('grants.grantDetails.organizationDetails.website')} testId="organization-website">
          {organisation.website ? (
            <a href={organisation.website} target="_blank" rel="noopener noreferrer">
              {organisation.website}
            </a>
          ) : (
            t('typography.notAvailable')
          )}
        </CardContent>
        <CardContent title={t('grants.grantDetails.organizationDetails.email')} testId="organization-email">
          {organisation.orgEmail ? (
            <a href={`mailto:${organisation.orgEmail}`}>{organisation.orgEmail}</a>
          ) : (
            t('typography.notAvailable')
          )}
        </CardContent>
        <CardContent title={t('grants.grantDetails.organizationDetails.country')} testId="organization-country">
          <Flex align="center" gap={8}>
            <FlagAvatar code3={organisation.countryCode} />
            <span data-testid="organization-country-code">{countryName}</span>
          </Flex>
        </CardContent>
        <CardContent title={t('grants.grantDetails.organizationDetails.address')} testId="organization-address">
          {valueOrNotAvailableString(organisation.address)}
        </CardContent>
        <CardContent title={t('grants.grantDetails.organizationDetails.registry')} testId="organization-registry-name">
          {valueOrNotAvailableString(organisation.registryName)}
        </CardContent>
        <CardContent title={t('grants.grantDetails.organizationDetails.registryId')} testId="organization-registry-id">
          {valueOrNotAvailableString(organisation.organisationIdInRegistry)}
        </CardContent>
      </CardDashboard>
    </Flex>
  )
}
